import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash, FileArrowDown } from "@phosphor-icons/react";
import { downloadCsv } from "./PreTradingTab";
import RuleNote from "./RuleNote";

const ENGINES = [
  { id: "le1200", label: "Up to 1200cc" },
  { id: "1201_1500", label: "1201–1500cc" },
  { id: "ge1501", label: "1501cc and over" },
  { id: "ev", label: "Electric Vehicle" },
];

export default function MileageTab({ onChange }) {
  const [data, setData] = useState({ items: [], ytd_km: 0, ytd_euro: 0, unreported_count: 0 });
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), purpose: "", from_location: "", to_location: "", distance_km: "", engine_category: "ge1501", reported_ros: false });

  const load = useCallback(async () => { const r = await api.get("/compliance/mileage"); setData(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/compliance/mileage", { ...form, distance_km: Number(form.distance_km) });
      setForm({ ...form, purpose: "", from_location: "", to_location: "", distance_km: "" });
      load(); onChange && onChange();
    } catch (err) { alert(err?.response?.data?.detail || err.message); }
  };

  const toggle = async (id) => { await api.put(`/compliance/mileage/${id}/reported`); load(); onChange && onChange(); };
  const del = async (id) => { await api.delete(`/compliance/mileage/${id}`); load(); onChange && onChange(); };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-6">
        <div className="eyebrow mb-1">Log a journey</div>
        <div className="text-xs text-white/45 mb-4">Civil Service 2025 rates. EVs use the 1201–1500cc band. Rate auto-applies by cumulative annual distance.</div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div><label>Date</label><input required type="date" data-testid="mil-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div className="md:col-span-2"><label>Purpose</label><input data-testid="mil-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Client meeting" /></div>
          <div><label>From</label><input data-testid="mil-from" value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} /></div>
          <div><label>To</label><input data-testid="mil-to" value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} /></div>
          <div><label>Distance (km)</label><input required type="number" step="0.1" data-testid="mil-km" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} /></div>
          <div className="md:col-span-2"><label>Engine / EV</label><select data-testid="mil-engine" value={form.engine_category} onChange={(e) => setForm({ ...form, engine_category: e.target.value })}>{ENGINES.map((en) => <option key={en.id} value={en.id}>{en.label}</option>)}</select></div>
          <button type="submit" data-testid="mil-add" className="btn-gold"><Plus weight="bold" size={14} /> Log journey</button>
        </form>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-6 text-sm">
          <div><span className="text-white/50">YTD distance:</span> <span className="mono" data-testid="mil-ytd-km">{Number(data.ytd_km).toLocaleString()} km</span></div>
          <div><span className="text-white/50">YTD claim:</span> <span className="mono text-[#FFB020]" data-testid="mil-ytd-euro">€{Number(data.ytd_euro).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
        </div>
        <button onClick={() => downloadCsv("/compliance/mileage.csv", "mileage-log.csv")} data-testid="mil-export" className="btn-ghost text-sm"><FileArrowDown size={16} /> Export CSV</button>
      </div>

      <div className="glass rounded-xl overflow-auto">
        <table className="data">
          <thead><tr><th>Date</th><th>Purpose</th><th className="text-right">KM</th><th>Engine</th><th className="text-right">Cum. KM</th><th className="text-right">Claim</th><th>ROS</th><th /></tr></thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="8" className="text-center text-white/40 py-8">No journeys logged.</td></tr>
            ) : data.items.map((e) => (
              <tr key={e.entry_id} data-testid={`mil-row-${e.entry_id}`}>
                <td className="mono text-xs">{e.date}</td>
                <td>{e.purpose || "—"}</td>
                <td className="num">{Number(e.distance_km).toFixed(1)}</td>
                <td className="text-white/70 text-xs">{e.engine_label}</td>
                <td className="num text-white/60">{Number(e.cumulative_km).toFixed(0)}</td>
                <td className="num text-[#FFB020]">€{Number(e.amount).toFixed(2)}</td>
                <td>
                  <button onClick={() => toggle(e.entry_id)} data-testid={`mil-ros-${e.entry_id}`} className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${e.reported_ros ? "badge-pass" : "badge-warn"}`}>{e.reported_ros ? "Reported" : "Pending"}</button>
                </td>
                <td><button onClick={() => del(e.entry_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`mil-del-${e.entry_id}`}><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RuleNote id="ie_mileage" />
    </div>
  );
}
